import type { LucideIcon } from 'lucide-react';
import type { CategoryShowcaseProps } from '@/components/organisms/CategoryShowcase';
import { CATEGORY_LABELS, CATEGORY_ORDER, type CategorySlug } from '@/config/categories';
import { getCategoryIcon } from './categoryIconMapping';

export interface CategoryWithIcon {
  id: string;
  name: string;
  slug: string;
  iconName: string;
  icon: LucideIcon;
}

/** Lucide icon name accepted by the `Icon` primitive the showcase renders. */
type CategoryIconName = CategoryShowcaseProps['categories'][number]['icon'];

/**
 * Per-category presentation: the icon each surface renders and the one-line
 * tagline the hub hero + homepage showcase card use.
 *
 * Keyed by `CategorySlug`, so adding a category to the closed ten without
 * giving it an icon and a tagline is a **compile error** rather than a hub
 * page that throws at request time (`getHubDefinition` needs both).
 * Labels are NOT repeated here — they come from `CATEGORY_LABELS`.
 *
 * @since WEB-606 (extended from six to the canonical ten)
 */
const CATEGORY_PRESENTATION: Readonly<
  Record<CategorySlug, { iconName: CategoryIconName; tagline: string }>
> = {
  music: { iconName: 'Music', tagline: 'Live performances & concerts' },
  sports: { iconName: 'Trophy', tagline: 'Games, matches & fitness' },
  food: { iconName: 'UtensilsCrossed', tagline: 'Tastings, pop-ups & dining' },
  'science-tech': { iconName: 'Cpu', tagline: 'Meetups, demos & hackathons' },
  art: { iconName: 'Palette', tagline: 'Galleries, shows & exhibits' },
  drinks: { iconName: 'Wine', tagline: 'Tastings, bars & socials' },
  'business-networking': { iconName: 'Briefcase', tagline: 'Mixers, panels & pitch nights' },
  'community-culture': { iconName: 'Users', tagline: 'Markets, festivals & gatherings' },
  wellness: { iconName: 'HeartPulse', tagline: 'Yoga, runs & mindfulness' },
  'hobbies-interests': { iconName: 'Puzzle', tagline: 'Clubs, crafts & game nights' },
};

const CORE_LIST: { id: string; name: string; slug: string; iconName: string }[] =
  CATEGORY_ORDER.map((slug) => ({
    id: slug,
    name: CATEGORY_LABELS[slug],
    slug,
    iconName: CATEGORY_PRESENTATION[slug].iconName,
  }));

/**
 * Homepage category cards: each `href` is a canonical `/category/<slug>` path
 * served by `app/(city)/category/[slug]/page.tsx`. Slugs are the canonical
 * ten declared in `@loci/types` (`CANONICAL_CATEGORY_SLUGS`, COR-37).
 */
export const HOMEPAGE_CATEGORY_SHOWCASE_ITEMS: CategoryShowcaseProps['categories'] =
  CATEGORY_ORDER.map((slug) => ({
    name: CATEGORY_LABELS[slug],
    slug,
    href: `/category/${slug}`,
    icon: CATEGORY_PRESENTATION[slug].iconName,
    tagline: CATEGORY_PRESENTATION[slug].tagline,
  }));

export const CATEGORIES: CategoryWithIcon[] = CORE_LIST.map((c) => ({
  ...c,
  icon: getCategoryIcon(c.iconName),
}));
